import { forwardRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';

interface VideoOverlayProps {
  src: string;
  isVisible: boolean;
  isPlaying: boolean;
  participantName: string;
  onTogglePlay: () => void;
  onEnded: () => void;
}

export const VideoOverlay = forwardRef<HTMLVideoElement, VideoOverlayProps>(
  function VideoOverlay(
    { src, isVisible, isPlaying, participantName, onTogglePlay, onEnded },
    ref
  ) {
    useEffect(() => {
      const video = (ref as React.RefObject<HTMLVideoElement>)?.current;
      if (!video) return;

      video.addEventListener('ended', onEnded);
      return () => video.removeEventListener('ended', onEnded);
    }, [ref, onEnded]);

    return (
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.9 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-6 right-6 z-50"
          >
            <div
              className={`relative w-80 rounded-xl overflow-hidden shadow-2xl cursor-pointer ring-2 transition-all ${
                isPlaying
                  ? 'ring-green-500/60 shadow-green-500/20'
                  : 'ring-border/40'
              }`}
              onClick={onTogglePlay}
            >
              <video
                ref={ref}
                src={src}
                className="w-full aspect-video bg-black object-cover"
                playsInline
                preload="auto"
              />

              <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-3 py-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {isPlaying && (
                      <span className="relative flex h-2.5 w-2.5">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                      </span>
                    )}
                    <span className="text-xs font-medium text-white/90">
                      {participantName}
                    </span>
                  </div>
                  {isPlaying && (
                    <span className="text-[10px] font-semibold tracking-wider uppercase text-green-400">
                      Live
                    </span>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }
);
